import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase.js';
import { useAuth } from '../context/AuthContext'; 

// Components & Assets
import Star from './star.jsx';
import LocationSVg from '../assets/location-sign-svgrepo-com.svg';
import replysvg from '../assets/reply.svg';
import newtrash from '../assets/blackbin.svg';
import finallike from '../assets/finallike.svg';
import finalreport from '../assets/finalreport.svg';
import invitedsvg from '../assets/invitesvg.svg';
import infosvg from '../assets/infosvg.svg';
import convertedSvg from '../assets/convertsvg.svg';

export default function CustomerReview({
  id, countr, img, author, date,
  stars, text, userid, isbusiness = false,
  businessimg, ReplierCompany, 
 user_id, submission_type, title, isPending = false, replies: incomingReplies = [], isActivity = false, website
}) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const AVATAR_COLORS = ['#2F855A', '#D69E2E', '#805AD5', '#DD6B20', '#3182CE', '#1E7D6B'];

  // --- State Management ---
  const [IsReply, setIsReply] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  
  const [localReplies, setLocalReplies] = useState(incomingReplies); 
  const [hasLiked, setHasLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [userRow, setUserRow] = useState(null); 

  const textareaRef = useRef(null);

  const notify = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: '' }), 3000);
  };

  // --- Like Sync Logic ---
  useEffect(() => {
    const fetchLikeData = async () => {
      if (!id) return;
      try {
        const { count, error: countError } = await supabase
          .from('likes')
          .select('*', { count: 'exact', head: true })
          .eq('review_id', id);
        if (!countError) setLikes(count || 0);

        if (currentUser) {
          const { data } = await supabase
            .from('likes')
            .select('id')
            .match({ review_id: id, user_id: currentUser.id })
            .maybeSingle();
          if (data) setHasLiked(true);
        }
      } catch (err) { console.error("Like sync error:", err); }
    };
    fetchLikeData();
  }, [id, currentUser]);

  const handleLike = async () => {
    if (isPending) return;

    if (!currentUser) return notify('Sign in to like', 'error');
    const wasLiked = hasLiked;
    setHasLiked(!wasLiked);
    setLikes(prev => wasLiked ? Math.max(0, prev - 1) : prev + 1);

    try {
      if (wasLiked) {
        await supabase.from('likes').delete().match({ review_id: id, user_id: currentUser.id });
      } else {
        await supabase.from('likes').insert([{ review_id: id, user_id: currentUser.id }]);
      }
    } catch (err) {
      setHasLiked(wasLiked);
      setLikes(prev => wasLiked ? prev + 1 : Math.max(0, prev - 1));
    }
  };

  const handleDeleteReview = async () => {
    setReplyLoading(true);
    try {
      const { error } = await supabase.functions.invoke('delete-review', {
        body: { reviewId: id }
      });
      if (error) throw error;
      notify("Review flagged for deletion.");
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      notify("Moderation request failed", "error");
    } finally { setReplyLoading(false); }
  };

  const handleReportSubmit = async () => {
   if (isPending) return;
    if (!reportReason.trim()) return;
    setReplyLoading(true);
    try {
      const { error } = await supabase.from('reports').insert({
        review_id: id, reporter_user_id: currentUser.id, reason: reportReason.trim()
      });
      if (error) {
        notify(error.code === '23505' ? 'Already reported' : 'Error submitting report', 'error');
      } else {
        notify('Flagged for moderation');
        setIsReporting(false);
        setReportReason('');
      }
    } finally { setReplyLoading(false); }
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim() || !currentUser) return;
    setReplyLoading(true);
    const { data, error } = await supabase
      .from('replies')
      .insert([{ review_id: id, content: replyText.trim(), author_id: currentUser.id }])
      .select(`id, content, created_at, author_id`)
      .single();
    if (!error && data) {
      setLocalReplies(prev => [...prev, data]);
      setReplyText('');
      setIsReply(false);
      notify("Official reply posted");
    }
    setReplyLoading(false);
  };

  const formatDate = (isoString) => isoString ? new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  const avatarBg = useMemo(() => {
    const name = author || 'User';
    const sum = name.split('').reduce((s, char) => s + char.charCodeAt(0), 0);
    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
  }, [author]);

  const handleInputHeight = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const starElements = Array.from({ length: 5 }, (_, i) => (
    <Star key={i} width={'w-[12px] xs:w-[18px]'} boxheight={'h-[20px] xs:h-[28px]'} boxwidth={'w-[20px] xs:w-[28px]'} bgColor={i < stars ? `bg-${['one', 'two', 'three', 'four', 'five'][stars - 1]}` : 'bg-gray-300'} />
  ));


  return (
    <article className={`w-full max-w-[95%] ip:max-w-[375px] mx-auto  ${isReporting ? "py-0 xs:py-0 pt-[20px] xs:pt-[24px]" : "py-[20px] xs:py-[24px]"} bg-white border border-slate-200 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] rounded-2xl transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6 relative xs:max-w-[450px] xl:max-w-[450px] 2xl:max-w-[630px] lg:w-[600px]`}>
      
      {/* Toast Notification */}
      {toast.message && (
        <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full shadow-lg z-50 text-[10px] font-bold tracking-tight animate-bounce-short text-white whitespace-nowrap ${toast.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between px-[16px] xl:px-[32px] gap-y-2">
        
        <div className={`flex  ${submission_type === "bwt" ? "items-center" : "items-start"} gap-[10px] sp:gap-[16px]`}>
       <div 
  onClick={() => user_id && navigate(`/users/${userid}`)} 
  className={`shrink-0 w-[36px] h-[36px] lg:w-[48px] lg:h-[48px] rounded-full relative ${userid ? 'cursor-pointer' : 'cursor-default'}`} 
  style={{ backgroundColor: (img || userRow?.img) ? 'transparent' : avatarBg }}
>
  {(img || userRow?.img) ? (
    <img src={img || userRow?.img} alt="" className="w-full h-full object-cover rounded-full" />
  ) : (
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="text-white text-[20px] lg:text-[28px] mt-[4px] font-staat">
        {author?.charAt(0).toUpperCase()}
      </span>
    </div>
  )}
</div>
          <div className="flex flex-col">
            <span onClick={() => user_id && navigate(`/users/${userid}`)} className={`text-slate-900  text-[12px] sp:text-[14px] font-poppins font-semibold tracking-wide ${user_id ? 'hover:underline cursor-pointer' : ''} truncate max-w-[110px] ip:max-w-none`}>
              {author} 
            </span>
            
            <div className='flex flex-wrap items-center mt-[2px] gap-x-3 gap-y-1'>
    {user_id === null && submission_type !== 'bwt' && (
  <div className="flex items-center gap-[4px] relative group">
    <span className="text-[10px] sp:text-[11px] text-[#494949] font-poppins tracking-[1px]">
      Guest
    </span>
    
    
    <div className="relative flex items-center">
      <img className='w-3 cursor-pointer opacity-60' src={infosvg} alt="Info" />
      
      {/* Tooltip - Styled for Forensic Trust */}
      <div className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-[220px] p-4 bg-[#32596E] text-white rounded-xl shadow-2xl z-[999] pointer-events-none transition-all duration-200">
        <div className="text-[10px] font-bold mb-1.5 uppercase tracking-widest text-[#23C1A5]">Zero-Friction Entry</div>
        <p className="text-[10px] leading-[1.4] opacity-90 whitespace-normal font-poppins">
          Client chose to leave feedback via a secure invite link without requiring a social login or account creation.
        </p>
        
        {/* Centered Arrow */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-[#32596E]"></div>
      </div>
    </div>
  </div>
)}
              <div className={`${!countr || countr === "Unknown" ? 'hidden' : 'flex'} items-center gap-[4px]`}>
                <img className="w-[10px] opacity-70" src={LocationSVg} alt="" />
                <span className="text-[#494949] text-[9px] font-spartan tracking-[1px] font-medium uppercase">{userRow?.country || countr}</span>
              </div>
            </div>
          </div>
        </div>
        <span className="text-slate-400 text-[9px] sp:text-[10px] font-poppins ml-auto sp:ml-0">{formatDate(date)}</span>
      </div>

      {/* Stars & Badge Row */}
      <div className="mt-[15px] px-[16px] xl:px-[32px] flex flex-wrap gap-x-4 gap-y-2 items-center">
        <div className="flex items-center gap-[0.1rem] sp:gap-[0.2rem]">{starElements}</div>
{isActivity && <span className="text-xs text-[#42CDB4] font-bold ml-1">Reviewed <span className="text-[#323232]">{website}</span></span> }
     {submission_type === "invited" ? (
<div className="flex items-center gap-[6px] py-0.5 whitespace-nowrap relative group">
  <img className="w-3 sp:w-auto" src={invitedsvg} alt="Invited" />
  <span className="text-[9px] sp:text-[11px] text-[#494949] font-poppins tracking-[1.5px] uppercase">Invited</span>
  
  {/* Wrap the icon in a relative div to anchor the tooltip exactly over it */}
  <div className="relative flex items-center">
    <img className='w-3 cursor-pointer opacity-60' src={infosvg} alt="Info" />
    
    {/* Tooltip - Increased Z-index and Fixed Padding */}
    <div className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-[240px] p-4 bg-[#32596E] text-white rounded-xl shadow-2xl z-[999] transition-all duration-200 pointer-events-none">
      <div className="text-[10px] font-bold mb-1.5 uppercase tracking-widest text-[#23C1A5]">Direct Invitation</div>
      <p className="text-[10px] leading-[1.4] opacity-90 whitespace-normal font-poppins">
        Submitted via a single-use secure link sent directly to the client's verified email address.
      </p>
      
      {/* Centered Arrow */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-[#32596E]"></div>
    </div>
  </div>
</div>

) : submission_type === "bwt" ? (
  <div className="flex items-center gap-[6px] py-0.5 whitespace-nowrap relative group">
    <img className="w-3 sp:w-auto" src={convertedSvg} alt="Converted" /> 
    <span className="text-[9px] sp:text-[11px] text-[#494949] font-poppins tracking-[1.5px] uppercase font-regular">Converted</span>
    <img className='w-3 cursor-pointer opacity-60' src={infosvg} alt="Info" />

    {/* Converted Tooltip - Added P-3 and Max-W */}
    <div className="invisible group-hover:visible absolute bottom-full left-0 mb-3 w-[220px] p-3 bg-[#32596E] text-white rounded-xl shadow-2xl z-[150] transition-all duration-200">
      <div className="text-[10px] font-bold mb-1 uppercase tracking-widest text-[#1E7D6B] brightness-150">Forensic Conversion</div>
      <p className="text-[10px] leading-relaxed opacity-90 whitespace-normal">
        Hand-verified from archived chat logs or invoices with explicit client authorization.
      </p>
      <div className="absolute top-full left-4 border-[6px] border-transparent border-t-[#32596E]"></div>
    </div>
  </div>
) : null}
        
      </div>

      {/* Body Content */}
      <div className="px-[16px] xl:px-[32px] mt-[12px] sp:mt-[15px]">
        <div className="font-poppins font-bold leading-tight text-[14px] sp:text-[16px] text-slate-900 mb-2">{title}</div>
        <div className="text-[13px] sp:text-[14px] text-slate-600 font-poppins leading-relaxed">{text}</div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-row px-[16px] xl:px-[32px] justify-between mt-[20px] pt-4 border-t border-slate-50">
        <div onClick={handleLike} className="flex items-center gap-[6px] cursor-pointer group">
          <img className={`w-[13px] sp:w-[14px] transition-all duration-300 ${hasLiked ? 'brightness-100' : 'grayscale opacity-50 hover:opacity-100'}`} src={finallike} alt="Like" />
          <span className={`font-spartan text-[11px] font-bold ${hasLiked ? 'text-blue-600' : 'text-slate-400'}`}>
            {likes > 0 ? likes : ''}
          </span>
        </div>

        <div className="flex items-center gap-4 sp:gap-6">
          {isbusiness && localReplies.length < 1 && (
            <div onClick={() => { setIsReply(!IsReply); setIsReporting(false); }} className="flex items-center gap-[6px] cursor-pointer text-slate-400 hover:text-slate-900 transition-colors">
              <img className="w-[14px] opacity-50" src={replysvg} alt="" />
              <span className="font-spartan text-[11px] font-bold hidden sp:inline">Reply</span>
            </div>
          )}
          {currentUser?.id && user_id && currentUser.id === user_id && (
            <button onClick={handleDeleteReview} disabled={replyLoading} className="group">
              <img className="w-[13px] sp:w-[14px] opacity-40 group-hover:opacity-100" src={newtrash} alt="Delete" />
            </button>
          )}
  <div 
  onClick={() => { 
    // ⚡️ Only allow reporting if the review is NOT pending
    if (!isPending) {
      setIsReporting(!isReporting); 
      setIsReply(false); 
    }
  }} 
  className={`group ${isPending ? 'cursor-not-allowed' : 'cursor-pointer'}`}
>
  <img 
    className={`w-[13px] sp:w-[14px] transition-all ${
      isPending 
        ? 'opacity-10 grayscale' // ⚡️ Make it look clearly disabled
        : isReporting 
          ? 'opacity-100' 
          : 'opacity-40 hover:opacity-100'
    }`} 
    src={finalreport} 
    alt="Report" 
  />
</div>
        </div>
      </div>

      {/* Sections: Report & Reply */}
      {isReporting && (
        <div className="flex flex-col gap-3 px-[16px] xl:px-[32px] rounded-b-[12px] py-4 mt-4 bg-slate-50 border-t border-slate-100">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Moderation Request</span>
          <textarea autoFocus onInput={handleInputHeight} value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="w-full border border-slate-200 p-3 rounded-lg outline-none text-[13px] bg-white" placeholder="Reason for flagging..." />
          <div className="flex justify-end gap-3">
            <button onClick={() => setIsReporting(false)} className="text-[11px] font-medium text-slate-400">Cancel</button>
            <button onClick={handleReportSubmit} disabled={replyLoading || !reportReason.trim()} className="px-4 py-1.5 bg-red-500 text-white rounded-md text-[11px] font-bold uppercase">Submit</button>
          </div>
        </div>
      )}

      {IsReply && (
        <div className="flex flex-col gap-4 px-[16px] xl:px-[32px] pt-6 mt-4">
          <textarea ref={textareaRef} onInput={handleInputHeight} value={replyText} onChange={(e) => setReplyText(e.target.value)} className="w-full border border-slate-200 p-3 rounded-lg outline-none text-[13px] sp:text-[14px]" placeholder="Write an official response..." />
          <div className="flex justify-end gap-3">
            <button onClick={() => setIsReply(false)} className="text-[12px] font-medium text-slate-500">Cancel</button>
            <button onClick={handleReplySubmit} disabled={replyLoading || !replyText.trim()} className="px-5 py-2 bg-slate-900 text-white rounded-lg text-[12px] font-bold">Post Reply</button>
          </div>
        </div>
      )}

      {/* Replies List */}
      {localReplies && localReplies.length > 0 && (
        <div className="flex flex-col mt-4 gap-4 px-[16px] xl:px-[32px] pt-6 border-t border-slate-100">
          {localReplies.map((reply) => (
            <div key={reply.id} className="flex flex-col gap-2">
              <div className="flex justify-between  items-center">
                <div className='flex flex-row items-center gap-[10px]'>
                  <div className='shrink-0 w-[24px] h-[24px] sp:w-[28px] sp:h-[28px] rounded-full bg-slate-200 overflow-hidden'>
                    {businessimg ? <img className='w-full h-full object-cover' src={businessimg} alt="" /> : <div className="w-full h-full flex items-center justify-center bg-slate-900 text-white text-[9px]">{ReplierCompany?.charAt(0)}</div>}
                  </div>
                  <span className="font-bold text-[12px] sp:text-[13px] text-slate-900">Reply from {ReplierCompany || "Business"}</span>
                </div>
                <span className="text-[9px] text-slate-400">{formatDate(reply.created_at || reply.date)}</span>
              </div>
              <p className="text-[12px] sp:text-[13px] text-slate-600 italic leading-relaxed pl-[0px] sp:pl-[0px]">{reply.content || reply.text}</p>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes bounce-short {
          0%, 100% { transform: translate(-50%, 0); opacity: 1; }
          50% { transform: translate(-50%, -5px); }
        }
        .animate-bounce-short {
          animation: bounce-short 0.5s ease-out;
        }
      `}</style>
    </article>
  );
}